import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, useSlots, watch } from 'vue';
import TrashSvg from '@/assets/icons/trash.svg';
import BaseText from "@/components-UI/BaseText.vue";
import BaseChipCollection from "@/components-UI/BaseChipCollection.vue";
import BaseTooltip from "@/components-UI/BaseTooltip.vue";
import { vMaska } from "maska/vue";

type Chip = { [key: string]: unknown } | string;


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseInput',
  props: {
    value: null,
    chipsValue: null,
    label: null,
    type: { default: 'text' },
    placeholder: null,
    disabled: { type: Boolean },
    readonly: { type: Boolean },
    loading: { type: Boolean },
    inputBan: { type: Boolean },
    hideInputIfValue: { type: Boolean },
    clearable: { type: Boolean },
    clearableType: { default: 'inner' },
    view: { default: 'border-bottom' },
    size: { default: 'large' },
    smallSize: { type: Boolean },
    multipleChips: { type: Boolean },
    isChip: { type: Boolean },
    isDeletableChips: { type: Boolean, default: true },
    chipMaxWidth: { default: '250px' },
    maxChips: null,
    chipsTooltipWidth: null,
    chipAddTrigger: { default: 'Enter' },
    chipKey: null,
    template: null,
    mask: null,
    isValid: { type: Boolean },
    isTeleportChipTooltip: { type: Boolean },
    inputTextAlign: null,
    minNumber: null,
    maxNumber: null,
    minLength: null,
    maxLength: null,
    showValueTitle: { type: Boolean }
  },
  emits: [
    'update:value',
    'update:chips',
    'input',
    'focus',
    'blur',
    'clear',
    'change',
    'delete-chip',
],
  setup(__props: any, { emit: emits }) {

const props = __props as { value: any, chipsValue?: Chip[], label?: string, type: string, placeholder?: string, disabled?: boolean, readonly?: boolean, loading?: boolean, inputBan?: boolean, hideInputIfValue?: boolean, clearable?: boolean, clearableType: 'inner' | 'out', view: 'border-bottom' | 'outlined' | 'empty', size: 'large' | 'medium' | 'small', smallSize?: boolean, multipleChips?: boolean, isChip?: boolean, isDeletableChips: boolean, chipMaxWidth: string, maxChips?: number, chipsTooltipWidth?: string, chipAddTrigger: 'Enter' | 'Space', chipKey?: string, template?: (item: Chip) => string, mask?: string | {}, isValid?: boolean, isTeleportChipTooltip?: boolean, inputTextAlign?: 'start' | 'center' | 'end', minNumber?: number, maxNumber?: number, minLength?: number, maxLength?: number, showValueTitle?: boolean }



const slots = useSlots();

const isHandleLabelMove = ref<boolean>(false);
const chips = ref<Chip[]>([]);
const inputRef = ref<HTMLInputElement | null>(null);
const isValueTruncated = ref<boolean>(false);

const getClasses = computed<string[]>(() => {
    return [
        props.view === 'outlined' && 'is-outlined',
        props.view === 'border-bottom' && 'is-border-bottom',
        props.view === 'empty' && 'is-empty',
        props.disabled && 'is-disabled',
        props.loading && 'is-loading',
        props.inputBan && 'is-input-ban',
        props.readonly && 'is-readonly',
        props.smallSize && 'is-small',
        props.size === 'small' && 'is-size-small',
        props.size === 'medium' && 'is-size-medium',
        props.size === 'large' && 'is-size-large',
        props.isValid && 'is-valid',
    ];
});

const isShowClearable = computed<boolean>(() => {
    return props.clearable && (props.value || chips.value.length) && !props.readonly && !props.disabled;
});

const isLabelMove = computed<boolean>(() => {
    return props.label &&
        (
            isHandleLabelMove.value ||
                props.value ||
                    chips.value.length
        );
});

const isShowInput = computed<boolean>(() => {
    if (props.hideInputIfValue) return false;
    if (props.multipleChips || props.isChip) return true;
    return !(chips.value.length >= 1);
});

const getChipsNames = computed<Chip[]>(() => {
    return chips.value.map((item: Chip) => props.template ? props.template(item) : props.chipKey ? item[props.chipKey] : item);
});

/**
 * Директива v-maska (maska v3) после наложения маски пере-dispatch-ит событие
 * ввода, поэтому один ввод пользователя приходит в handleInput дважды с
 * ОДИНАКОВЫМ значением — родитель получал два `update:value` и два `input`
 * (два запроса на символ в поиске-по-вводу).
 *
 * Гасим только такой повтор: помним отправленное значение и сбрасываем память
 * через setTimeout(0). Пере-dispatch маски происходит синхронно, в том же
 * макротаске, а следующий ввод пользователя — уже в другом, поэтому повторный
 * ввод того же текста позже пройдёт как обычно. Значения, отличающиеся от
 * предыдущего, не фильтруются никогда.
 */
let lastEmittedValue: string | null = null;

const handleInput = (event: Event): void => {
    const target = event.target as HTMLInputElement;

    if (props.type === 'number') {
        if (isNaN(Number(target.value))) {
            target.value = '0';
        }

        if (props.minNumber && Number(target.value) < props.minNumber) {
            target.value = String(props.minNumber);
        }

        if (props.maxNumber && Number(target.value) > props.maxNumber) {
            target.value = String(props.maxNumber);
        }

        target.value = target.value.replace(/[^0-9.]/g, '');
    }

    if (lastEmittedValue === target.value) {
        return;
    }

    lastEmittedValue = target.value;
    setTimeout(() => {
        lastEmittedValue = null;
    }, 0);

    emits('update:value', target.value);
    emits('input', target.value);
};

const handleFocus = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    emits('focus', target.value);

    if (!(props.readonly || props.disabled)) {
        isHandleLabelMove.value = true;
    }
};

const handleBlur = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    emits('blur', target.value);

    if (!(props.readonly || props.disabled)) {
        isHandleLabelMove.value = false;
    }
};

const handleClear = (): void => {
    emits('update:value', '');
    emits('update:chips', []);
    emits('input', '');
    emits('focus', '');
    emits('blur', '');
    emits('change', '');
    emits('clear');
};

const handleChange = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    emits('change', target.value);
};

const updateValueTruncation = (): void => {
    const el = inputRef.value;
    isValueTruncated.value = !!el && el.scrollWidth > el.clientWidth;
};

const handleKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLInputElement;
    if (
        (props.multipleChips || props.isChip) &&
        target.value &&
        event.code === props.chipAddTrigger
    ) {
        chips.value.push(
            props.chipKey ? { [props.chipKey]: target.value } : target.value,
        );
        emits('update:value', '');
        emits('update:chips', chips.value);
    }
};

const handleDeleteChip = (event: { item: Chip, index: number }): void => {
    chips.value.splice(event.index, 1);
    emits('update:chips', chips.value);
    emits('delete-chip', event);
};

watch(() => props.chipsValue?.length, () => {
    if (props.multipleChips || props.isChip) {
        chips.value = props.chipsValue;
    }
}, { deep: true, immediate: true });

return { __sfc: true,props, emits, slots, isHandleLabelMove, chips, inputRef, isValueTruncated, getClasses, isShowClearable, isLabelMove, isShowInput, getChipsNames, lastEmittedValue, handleInput, handleFocus, handleBlur, handleClear, handleChange, updateValueTruncation, handleKeyDown, handleDeleteChip, TrashSvg, BaseText, BaseChipCollection, BaseTooltip, vMaska }
}

})